// react-table is extending from object, so we have to do that as well
/* eslint-disable @typescript-eslint/ban-types */
import React from 'react';
import { Row } from 'react-table';

import { SubHeader, GroupedRow, SubRow } from '.';

interface GroupedRowWithSubHeaderProps<D extends object> {
    /**
     * Index to apply as the `aria-rowindex` attribute - see {@link https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Row_Role}
     */
    ariaRowIndex: number;
    /**
     * Width for the caret icon
     */
    caretIconWidth: number;
    /**
     * Tailwind size to apply to text for grouped rows
     */
    groupTextSize: 'xs' | 'sm' | 'base' | 'lg';
    /**
     * Outer row height
     */
    rowHeight: number;
    /**
     * The react-table row instance
     */
    row: Row<D>;
    /**
     * Sub-header height
     */
    subHeaderHeight: number;
    /**
     * Sub-row height
     */
    subRowHeight: number;
    /**
     * The `prepareRow()` function returned by the react-table `useTable()` effect
     */
    prepareRow(row: Row<D>): void;
    /**
     * Tailwind size to apply to text
     */
    textSize: 'xs' | 'sm' | 'base' | 'lg';
}

/**
 * Renders a grouped table row. If the grouped row is expanded, also renders its sub-rows.
 * @param props - {@link GroupedRowWithSubHeaderProps}
 */
export function GroupedRowWithSubHeader<D extends object>({
    ariaRowIndex,
    caretIconWidth,
    subHeaderHeight,
    subRowHeight,
    groupTextSize,
    rowHeight,
    row,
    prepareRow,
    textSize,
}: GroupedRowWithSubHeaderProps<D>): JSX.Element {
    prepareRow(row);

    return (
        <>
            <GroupedRow<D>
                ariaRowIndex={ariaRowIndex}
                caretIconWidth={caretIconWidth}
                height={rowHeight}
                groupTextSize={groupTextSize}
                row={row}
            />
            {row.isExpanded &&
                row.subRows.map((subRow, subRowIndex) => {
                    prepareRow(subRow);

                    const { key: subRowKey } = subRow.getRowProps();

                    return (
                        <span key={subRowKey}>
                            {subRowIndex === 0 && (
                                <SubHeader<D>
                                    ariaRowIndex={
                                        ariaRowIndex + subRowIndex + 1
                                    }
                                    subHeaderHeight={subHeaderHeight}
                                    rowHeight={rowHeight}
                                    row={subRow}
                                    textSize={textSize}
                                />
                            )}
                            <SubRow<D>
                                ariaRowIndex={ariaRowIndex + subRowIndex + 2}
                                height={subRowHeight}
                                row={subRow}
                                textSize={textSize}
                            />
                        </span>
                    );
                })}
        </>
    );
}
